
import { toast } from "sonner";
import { performAISearch } from "./AISearchStrategy";
import { performBasicSearch } from "./BasicSearchStrategy";
import { performSeasonalSearch } from "./SeasonalSearchStrategy";
import { performEnhancedFallbackSearch, performBroadSearch } from "./FallbackSearchStrategy";
import { handleCoastaBravaSearch } from "./strategies/CoastaBravaSearchStrategy";
import { detectSeasonalIntent } from '../../../supabase/functions/ai-vacation-search/seasonalIntelligence';
import { enhanceQuery, analyzeQueryIntent } from "./QueryEnhancementService";
import { getSmartSearchQuery, suggestQueryImprovements } from "./SmartSearchHelper";
import { trackSearchMetrics } from "./SearchAnalyticsService";

export const executeSearchStrategy = async (
  query: string,
  setSearchResults: (results: any[]) => void
) => {
  const startTime = Date.now();
  const userSession = Math.random().toString(36).substring(7);
  let usedStrategy = 'unknown';
  let finalResults: any[] = [];
  
  console.log(`🚨 === SEARCH ORCHESTRATOR CALLED ===`);
  console.log(`🚨 Query received: "${query}"`);
  console.log(`🚨 Timestamp: ${new Date().toISOString()}`);
  
  try {
    console.log(`🔍 === INTELLIGENT SEARCH ORCHESTRATOR START ===`);
    console.log(`Original query: "${query}"`);
    console.log(`Session: ${userSession}`);
    
    // STEP 1: Analyze query intent and complexity
    const queryAnalysis = analyzeQueryIntent(query);
    console.log('🧠 Query analysis:', queryAnalysis);
    
    // STEP 2: Apply smart typo correction and improvements
    const smartQuery = getSmartSearchQuery(query);
    const actualQuery = smartQuery !== query ? smartQuery : query;
    
    if (smartQuery !== query) {
      console.log('🔧 Applied smart corrections:', query, '→', smartQuery);
      toast.info(`Typfout gecorrigeerd: "${smartQuery}"`);
    }
    
    // STEP 3: Try query enhancement for better results
    const enhancement = await enhanceQuery(actualQuery);
    if (enhancement) {
      console.log('✨ Query enhancement found:', enhancement);
    }
    
    // STEP 4: Get query suggestions for future improvement
    const improvements = suggestQueryImprovements(actualQuery);
    if (improvements.length > 0) {
      console.log('💡 Query improvement suggestions:', improvements);
    }
    
    // SPECIAL HANDLING FOR COSTA BRAVA - Always use Costa Brava strategy first
    const lowerQuery = actualQuery.toLowerCase();
    if (lowerQuery.includes('costa brava')) {
      console.log('🏖️ COSTA BRAVA DETECTED - Using Costa Brava Strategy FIRST');
      usedStrategy = 'costa_brava';
      try {
        const costaBravaResults = await handleCoastaBravaSearch(
          { type: 'region', info: { region: 'Costa Brava', country: 'Spanje' }, priority: 10 },
          null, // No price constraints from query parsing yet
          null, // No facilities constraints from query parsing yet
          null, // No travel constraints from query parsing yet
          actualQuery
        );
        
        if (costaBravaResults.length > 0) {
          console.log(`✅ Costa Brava search successful: ${costaBravaResults.length} results found`);
          finalResults = costaBravaResults;
          setSearchResults(costaBravaResults);
          toast.success(`${costaBravaResults.length} Costa Brava vakanties gevonden!`);
          
          // Track successful search
          await trackSearchMetrics({
            query: actualQuery,
            resultsCount: costaBravaResults.length,
            searchDuration: Date.now() - startTime,
            strategy: usedStrategy,
            success: true,
            userSession
          });
          return;
        } else {
          console.log('❌ Costa Brava search returned no results, trying fallback');
        }
      } catch (error) {
        console.error('❌ Costa Brava search error:', error);
      }
    }
    
    // ALWAYS try basic search first for location-based queries OR location + experience queries
    const hasLocationTerms = 
      lowerQuery.includes('portugal') || 
      lowerQuery.includes('spanje') || 
      lowerQuery.includes('italië') || 
      lowerQuery.includes('italie') ||
      lowerQuery.includes('griekenland') ||
      lowerQuery.includes('turkije') ||
      lowerQuery.includes('frankrijk') ||
      lowerQuery.includes('duitsland') ||
      lowerQuery.includes('oostenrijk') ||
      lowerQuery.includes('zwitserland') ||
      lowerQuery.includes('belgie') ||
      lowerQuery.includes('nederland') ||
      lowerQuery.includes('thailand') ||
      lowerQuery.includes('vietnam') ||
      lowerQuery.includes('indonesië') ||
      lowerQuery.includes('maleisië') ||
      lowerQuery.includes('singapore') ||
      lowerQuery.includes('japan') ||
      lowerQuery.includes('china') ||
      lowerQuery.includes('india') ||
      lowerQuery.includes('sri lanka') ||
      lowerQuery.includes('maldiven') ||
      // Populaire steden/eilanden
      lowerQuery.includes('kos') ||
      lowerQuery.includes('rhodos') ||
      lowerQuery.includes('kreta') ||
      lowerQuery.includes('santorini') ||
      lowerQuery.includes('mykonos') ||
      lowerQuery.includes('barcelona') ||
      lowerQuery.includes('madrid') ||
      lowerQuery.includes('rome') ||
      lowerQuery.includes('parijs') ||
      lowerQuery.includes('istanbul') ||
      lowerQuery.includes('dubai') ||
      lowerQuery.includes('costa brava');

    const hasExperienceTerms = 
      lowerQuery.includes('rondreis') ||
      lowerQuery.includes('stedentrip') ||
      lowerQuery.includes('safari') ||
      lowerQuery.includes('cruise') ||
      lowerQuery.includes('wintersport') ||
      lowerQuery.includes('wellness') ||
      lowerQuery.includes('cultureel') ||
      lowerQuery.includes('natuur') ||
      lowerQuery.includes('avontuur');
    
    if (hasLocationTerms) {
      console.log('🎯 LOCATION DETECTED - Using Basic Search Strategy First');
      console.log('🎯 DEBUG: hasLocationTerms=', hasLocationTerms, 'hasExperienceTerms=', hasExperienceTerms);
      console.log('🎯 DEBUG: Query contains thailand?', lowerQuery.includes('thailand'));
      console.log('🎯 DEBUG: Query contains rondreis?', lowerQuery.includes('rondreis'));
      usedStrategy = 'basic_location';
      const basicResults = await performBasicSearch(actualQuery);
      if (basicResults.length > 0) {
        console.log(`✅ Basic search successful: ${basicResults.length} results found`);
        finalResults = basicResults;
        setSearchResults(basicResults);
        
        // Provide more specific success message for location + experience queries
        if (hasLocationTerms && hasExperienceTerms) {
          toast.success(`${basicResults.length} ${hasExperienceTerms ? 'reis' : 'vakantie'}${basicResults.length === 1 ? '' : 's'} gevonden!`);
        } else {
          toast.success(`${basicResults.length} vakanties gevonden!`);
        }
        
        // Track successful search
        await trackSearchMetrics({
          query: actualQuery,
          resultsCount: basicResults.length,
          searchDuration: Date.now() - startTime,
          strategy: usedStrategy,
          success: true,
          userSession
        });
        return;
      } else {
        console.log('❌ Basic search returned no results for location query');
      }
    }
    
    // Check for refinement keywords (zwembad, all inclusive, etc.)
    const hasRefinementTerms = 
      lowerQuery.includes('zwembad') ||
      lowerQuery.includes('all inclusive') ||
      lowerQuery.includes('wellness') ||
      lowerQuery.includes('spa') ||
      lowerQuery.includes('restaurant') ||
      lowerQuery.includes('bar') ||
      lowerQuery.includes('maximaal') ||
      lowerQuery.includes('max') ||
      lowerQuery.includes('€') ||
      lowerQuery.includes('euro') ||
      lowerQuery.includes('families') ||
      lowerQuery.includes('kinderen') ||
      lowerQuery.includes('adults only');

    if (hasRefinementTerms) {
      console.log('🔧 REFINEMENT TERMS DETECTED - Using Basic Search for filtered results');
      const basicResults = await performBasicSearch(query);
      if (basicResults.length > 0) {
        console.log(`✅ Refinement search successful: ${basicResults.length} results found`);
        setSearchResults(basicResults);
        toast.success(`${basicResults.length} vakanties gevonden met jouw criteria!`);
        return;
      }
    }
    
    // Check for seasonal search
    console.log('Step 1: Checking for seasonal search...');
    const seasonalIntent = detectSeasonalIntent(query);
    if (seasonalIntent.isSeasonalQuery) {
      console.log('Detected seasonal query, using seasonal search strategy');
      const seasonalResults = await performSeasonalSearch(query);
      if (seasonalResults.length > 0) {
        console.log(`Seasonal search successful: ${seasonalResults.length} results found`);
        setSearchResults(seasonalResults);
        
        let message = `${seasonalResults.length} vakanties gevonden!`;
        if (seasonalIntent.month && seasonalIntent.weatherPreference === 'warm') {
          if (seasonalIntent.geographicRestrictions?.includes('outside_europe')) {
            message = `${seasonalResults.length} warme bestemmingen buiten Europa gevonden voor ${seasonalIntent.month}!`;
          } else {
            message = `${seasonalResults.length} warme bestemmingen gevonden voor ${seasonalIntent.month}!`;
          }
        } else if (seasonalIntent.weatherPreference === 'warm') {
          if (seasonalIntent.geographicRestrictions?.includes('outside_europe')) {
            message = `${seasonalResults.length} zonnige vakanties buiten Europa gevonden!`;
          } else {
            message = `${seasonalResults.length} zonnige vakanties gevonden!`;
          }
        }
        
        toast.success(message);
        return;
      }
    }

    // Try AI search only if no location terms detected
    if (!hasLocationTerms && !hasRefinementTerms && !hasExperienceTerms) {
      console.log('Step 2: Trying AI search for general query...');
      const aiResults = await performAISearch(query);
      if (aiResults.length > 0) {
        console.log(`AI search successful: ${aiResults.length} results found`);
        setSearchResults(aiResults);
        toast.success(`${aiResults.length} vakanties gevonden!`);
        return;
      }
    }

    // Try basic search if not done yet
    if (!hasLocationTerms && !hasRefinementTerms && !hasExperienceTerms) {
      console.log('Step 3: Trying basic search...');
      const basicResults = await performBasicSearch(query);
      if (basicResults.length > 0) {
        console.log(`Basic search successful: ${basicResults.length} results found`);
        setSearchResults(basicResults);
        toast.success(`${basicResults.length} vakanties gevonden!`);
        return;
      }
    }

    // Try enhanced fallback search
    console.log('Step 4: Trying enhanced fallback search...');
    usedStrategy = 'fallback';
    await handleFallbackSearch(actualQuery, setSearchResults, startTime, userSession);
  } catch (error: any) {
    console.error('❌ Search strategy execution error:', error);
    await trackSearchMetrics({
      query: query,
      resultsCount: 0,
      searchDuration: Date.now() - startTime,
      strategy: usedStrategy,
      success: false,
      userSession
    });
    await handleFallbackSearch(query, setSearchResults, startTime, userSession);
  }
};

const handleFallbackSearch = async (
  query: string, 
  setSearchResults: (results: any[]) => void,
  startTime: number = Date.now(),
  userSession: string = Math.random().toString(36).substring(7)
) => {
  try {
    const enhancedResults = await performEnhancedFallbackSearch(query);
    if (enhancedResults.length > 0) {
      console.log(`Enhanced fallback search successful: ${enhancedResults.length} results found`);
      setSearchResults(enhancedResults);
      toast.success(`${enhancedResults.length} vakanties gevonden!`);
    } else {
      console.log('Enhanced fallback search empty, checking for specific cases...');
      
      const lowerQuery = query.toLowerCase();
      const isSpecificLocationSearch = 
        lowerQuery.includes('afrika') || 
        lowerQuery.includes('safari door') ||
        lowerQuery.includes('australië') ||
        lowerQuery.includes('nieuw-zeeland') ||
        lowerQuery.includes('japan') ||
        lowerQuery.includes('china') ||
        lowerQuery.includes('brazilië') ||
        lowerQuery.includes('argentinië');
      
      if (isSpecificLocationSearch) {
        toast.error(`Deze bestemming zit momenteel niet in ons aanbod. Probeer eens een andere bestemming!`);
        setSearchResults([]);
        return;
      }

      const hasBudgetTerms = 
        lowerQuery.includes('max') || 
        lowerQuery.includes('maximaal') ||
        lowerQuery.includes('budget') ||
        lowerQuery.includes('€') ||
        lowerQuery.includes('euro') ||
        /\d+\s*(pp|per persoon)/.test(lowerQuery);
      
      if (hasBudgetTerms) {
        toast.error(`Geen vakanties gevonden binnen dit budget. Probeer een hoger budget of andere criteria!`);
        setSearchResults([]);
        return;
      }

      const hasSpecificType = 
        lowerQuery.includes('zonvakantie') ||
        lowerQuery.includes('zonnige') ||
        lowerQuery.includes('all inclusive') ||
        lowerQuery.includes('adults only') ||
        lowerQuery.includes('rondreis') ||
        lowerQuery.includes('stedentrip') ||
        lowerQuery.includes('wintersport');
      
      if (hasSpecificType) {
        toast.error(`Geen vakanties gevonden voor deze specifieke criteria. Probeer andere opties!`);
        setSearchResults([]);
        return;
      }

      console.log('Step 5: Trying broad search as last resort...');
      const broadResults = await performBroadSearch(query);
      if (broadResults.length > 0) {
        console.log(`Broad search successful: ${broadResults.length} results found`);
        setSearchResults(broadResults);
        toast.info(`Geen exacte match gevonden. Hier zijn enkele alternatieven (${broadResults.length} vakanties)`);
      } else {
        console.log('All search strategies failed - no results found');
        toast.error("Geen vakanties gevonden voor deze zoekopdracht");
        setSearchResults([]);
      }
    }
  } catch (error: any) {
    console.error('Fallback search error:', error);
    toast.error("Er ging iets mis bij het zoeken");
    setSearchResults([]);
  }
};
